import React, { useMemo, useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useDarkMode } from "@hooks/dark-mode";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

interface FaqItem {
    id: string;
    question: string;
    answer: string;
}

const accordionVariants = {
    collapsed: { height: 0, opacity: 0 },
    expanded: { height: "auto", opacity: 1 }
};
const accordionTransition = { type: "spring", stiffness: 170, damping: 26, mass: 0.9 } as const;

const FAQ: React.FC = () => {
    const { dark } = useDarkMode();
    const { t } = useTranslation();
    const navigate = useNavigate();

    const titleColor = dark ? "#ffffff" : "#111827";
    const subtitleColor = dark ? "#9CA3AF" : "#6B7280";
    const borderColor = dark ? "#3F3F46" : "#E5E7EB";
    const cardBg = dark ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.02)";
    const accent = dark ? "#80ABF9" : "#1673FF";

    const items: FaqItem[] = useMemo(() => ([
        {
            id: "how-works",
            question: t("home.faq.q1"),
            answer: t(
                "home.faq.a1",
            )
        },
        {
            id: "benefits",
            question: t("home.faq.q2"),
            answer: t(
                "home.faq.a2",
            )
        },
        {
            id: "privacy",
            question: t("home.faq.q3"),
            answer: t(
                "home.faq.a3",
            )
        },
        {
            id: "get-started",
            question: t("home.faq.q4"),
            answer: t(
                "home.faq.a4",
            )
        },
        {
            id: "transport-modes",
            question: t("home.faq.q5"),
            answer: t(
                "home.faq.a5",
            )
        }
    ]), [t]);

    const [openId, setOpenId] = useState<string | null>(null);

    const gridRef = useRef<HTMLDivElement>(null);
    const leftRef = useRef<HTMLDivElement>(null);
    const rightRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const updateMargins = () => {
            if (!gridRef.current || !leftRef.current || !rightRef.current) return;
            const isDesktop = window.matchMedia("(min-width: 768px)").matches;
            if (!isDesktop) {
                leftRef.current.style.marginTop = "0px";
                rightRef.current.style.marginTop = "0px";
                return;
            }
            const gridH = gridRef.current.clientHeight;
            const leftH = leftRef.current.clientHeight;
            const rightH = rightRef.current.clientHeight;
            leftRef.current.style.marginTop = `${(gridH - leftH) / 2}px`;
            rightRef.current.style.marginTop = `${(gridH - rightH) / 2}px`;
        };
        updateMargins();
        window.addEventListener('resize', updateMargins);
        return () => window.removeEventListener('resize', updateMargins);
    }, []);

    return (
        <section id="faq" className="w-full py-10 bg-Bg">
            <div className="px-4 mx-auto max-w-screen-xl">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-10 items-start md:h-[700px]" ref={gridRef}>
                    {/* Left copy */}
                    <div className="max-w-2xl mx-auto md:mx-0 text-center md:text-left" ref={leftRef}>
                        <div className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold mb-2 mx-auto md:mx-0" style={{ color: accent, border: `1px solid ${accent}` }}>
                            {t("home.faq.tag")}
                        </div>
                        <h2 className="text-5xl md:text-6xl tracking-tight font-medium mb-4 text-center md:text-left" style={{ color: titleColor }}>
                            {t("home.faq.title", "Frequently\nAsked\nQuestions").split("\n").map((line, idx) => (
                                <span key={idx} className="block">{line}</span>
                            ))}
                        </h2>
                        <p className="text-lg md:text-xl font-light mb-8 max-w-xs mx-auto md:mx-0 text-center md:text-left" style={{ color: subtitleColor }}>
                            {t(
                                "home.faq.subtitle",
                                "Got any questions? Let us know! Reach out and our team will get right back to you."
                            )}
                        </p>
                        <div className="flex justify-center md:justify-start">
                            <button
                                onClick={() => navigate("/contact")}
                                className="inline-flex items-center justify-center px-6 py-3 text-base font-semibold text-white rounded-xl"
                                style={{ backgroundColor: accent }}
                            >
                                {t("home.faq.contact")}
                            </button>
                        </div>
                    </div>

                    {/* Right accordion */}
                    <div className="space-y-4 pr-2" ref={rightRef}>
                        {items.map((item) => {
                            const isOpen = openId === item.id;
                            return (
                                <div
                                    key={item.id}
                                    className="rounded-2xl"
                                    style={{ border: `1px solid ${borderColor}`, backgroundColor: cardBg }}
                                >
                                    <button
                                        className="w-full flex items-center justify-between text-left px-6 py-5"
                                        onClick={() => setOpenId(isOpen ? null : item.id)}
                                    >
                                        <span className="text-lg md:text-xl font-medium" style={{ color: titleColor }}>
                                            {item.question}
                                        </span>
                                        {/* plus/minus icon */}
                                        <span
                                            aria-hidden
                                            className="ml-6 inline-flex items-center justify-center w-8 h-8 rounded-full"
                                            style={{ color: titleColor }}
                                        >
                                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <path d="M5 12h14" />
                                                <motion.path
                                                    d="M12 5v14"
                                                    animate={{ scaleY: isOpen ? 0 : 1 }}
                                                    transition={{ type: "spring", stiffness: 200, damping: 18 }}
                                                    style={{ transformOrigin: "center" }}
                                                />
                                            </svg>
                                        </span>
                                    </button>

                                    <AnimatePresence initial={false}>
                                        {isOpen && (
                                            <motion.div
                                                variants={accordionVariants}
                                                initial="collapsed"
                                                animate="expanded"
                                                exit="collapsed"
                                                transition={accordionTransition}
                                                className="px-6"
                                                style={{ overflow: "hidden" }}
                                            >
                                                <motion.div
                                                    initial={{ opacity: 0, y: 6 }}
                                                    animate={{ opacity: 1, y: 0 }}
                                                    exit={{ opacity: 0, y: -4 }}
                                                    transition={{ duration: 0.2, ease: "easeInOut" }}
                                                    className="pb-6"
                                                >
                                                    <p className="text-base md:text-lg font-light" style={{ color: subtitleColor }}>
                                                        {item.answer}
                                                    </p>
                                                </motion.div>
                                            </motion.div>
                                        )}
                                    </AnimatePresence>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
        </section>
    );
};

export default FAQ;


